// Client-side data export.
//
// Calls the `export_my_data` Supabase RPC (see
// supabase/migrations/2026-04-24-01-export-my-data.sql), which returns a
// single jsonb bundle of every row the user owns across the public schema.
// The wrapper below triggers a browser download so the user ends up with
// `slash-data-export-<timestamp>.json` on disk.
//
// ADR: docs/adr/0016-privacy-controls.md

import { supabase } from './supabase'
import { toAppError, type AppError } from './errors'
import { createLogger } from './logger'
import { logAudit } from './audit'

const log = createLogger('export')

export interface ExportResult {
  ok: boolean
  /** Present on success — what the user downloads. */
  bundle?: unknown
  /** Present on failure. */
  error?: AppError
}

/**
 * Fetch the export bundle. Does NOT trigger a download — use
 * `downloadMyData()` for the UI path. This split lets tests (and future
 * admin tooling) fetch the payload without a side effect.
 */
export async function fetchMyDataExport(): Promise<ExportResult> {
  try {
    const { data, error } = await supabase.rpc('export_my_data')
    if (error) {
      log.error('export_my_data RPC failed', error)
      return { ok: false, error: toAppError(error) }
    }
    return { ok: true, bundle: data }
  } catch (e) {
    log.error('export_my_data threw', e)
    return { ok: false, error: toAppError(e) }
  }
}

/**
 * Fetch + trigger a browser download.
 *
 * Fires an audit event on success (`action: 'data_exported'`; see note
 * below on the AuditEvent catalogue). Silent on failure — the caller is
 * expected to surface the error; no audit entry if the export didn't
 * happen.
 *
 * Return value: `null` on success, an `AppError` on failure. Kept in-band
 * so the UI can branch without try/catch.
 *
 * DOM side effect: creates an `<a download>` element, clicks it, revokes
 * the blob URL. No-op in non-browser contexts (SSR / tests without a
 * DOM) — callers running in those contexts should use `fetchMyDataExport`.
 */
export async function downloadMyData(): Promise<AppError | null> {
  const result = await fetchMyDataExport()
  if (!result.ok || !result.bundle) {
    return result.error ?? toAppError(new Error('Unknown export failure'))
  }

  try {
    const json = JSON.stringify(result.bundle, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const ts = new Date().toISOString().replace(/[:.]/g, '-').replace(/T/, '_').slice(0, 19)
    const filename = `slash-data-export-${ts}.json`

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Revoke in the next task — some browsers need the URL alive during
    // the click-dispatched download.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch (e) {
    log.error('download trigger failed', e)
    return toAppError(e)
  }

  // Fire-and-forget audit. Note: this requires extending the
  // `AuditEvent` union in `audit.ts` with a `data_exported` variant.
  // Done in this commit — see the matching edit to `audit.ts`.
  void logAudit({
    action: 'data_exported',
    resourceType: 'profile',
    // resource_id is the caller's own profile id; the RPC already enforces
    // that, so we don't need to look it up client-side.
    resourceId: 'self',
  })

  return null
}
